import React, { useCallback, useEffect, useState } from 'react';
import { Chess } from 'chess.js';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import ChessBoardView from '@/components/chess/ChessBoardView';
import { toast } from 'sonner';
import {
  ChevronRight, ChevronDown, Folder, Search, Sword, Loader2, ArrowLeft,
  SkipBack, ChevronLeft, ChevronRight as ChevRight, SkipForward, Check
} from 'lucide-react';

const START = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

function StepDot({ n, label, active, done }) {
  return (
    <div className="flex items-center gap-2">
      <div className={`w-8 h-8 rounded-full grid place-items-center text-sm font-bold ${active||done ? 'bg-indigo-600 text-white' : 'bg-slate-200 text-slate-500'}`}>
        {done ? <Check className="w-4 h-4"/> : n}
      </div>
      <span className={`text-lg font-semibold ${active||done ? 'text-slate-900' : 'text-slate-400'}`}>{label}</span>
    </div>
  );
}

// One selectable game row with lazy PGN navigation
function GameRow({ game, index, checked, onToggle }) {
  const [detail, setDetail] = useState(null);
  const [fens, setFens] = useState([game.starting_fen || START]);
  const [idx, setIdx] = useState(0);
  const [showPgn, setShowPgn] = useState(false);
  const [loading, setLoading] = useState(false);

  const ensureDetail = useCallback(async () => {
    if (detail) return fens;
    setLoading(true);
    try {
      const { data } = await api.get(`/games/${game.id}`);
      const c = new Chess(data.starting_fen || undefined);
      const f = [c.fen()];
      for (const m of (data.moves || [])) {
        if (m.fen) { f.push(m.fen); try { c.load(m.fen); } catch { /* ignore */ } continue; }
        try { c.move(m.san); f.push(c.fen()); } catch { break; }
      }
      setDetail(data); setFens(f);
      return f;
    } catch { return fens; } finally { setLoading(false); }
  }, [detail, fens, game.id]);

  const nav = async (target) => {
    const f = await ensureDetail();
    const last = (f.length || 1) - 1;
    setIdx((prev) => {
      if (target === 'first') return 0;
      if (target === 'last') return last;
      if (target === 'prev') return Math.max(0, prev - 1);
      return Math.min(last, prev + 1);
    });
  };

  const meta = [game.date && game.date !== '????.??.??' ? game.date : 'Unknown date', game.opening || game.event].filter(Boolean).join(', ');

  return (
    <div className="border-b border-slate-100 py-4">
      <label className="flex items-center gap-2 mb-2 cursor-pointer w-max" data-testid={`pw-select-${game.id}`}>
        <input type="checkbox" checked={checked} onChange={()=>onToggle(game.id)} className="w-4 h-4"/>
        <span className="text-sm font-semibold text-slate-700">Select</span>
      </label>
      <div className="flex gap-4">
        <div className="w-40 shrink-0">
          <ChessBoardView fen={fens[idx] || game.starting_fen || START} boardWidth={160} arePiecesDraggable={false}/>
          <div className="flex items-center justify-center gap-0.5 mt-1 text-indigo-600">
            <button data-testid={`pw-first-${game.id}`} onClick={()=>nav('first')} className="p-1 hover:bg-indigo-50 rounded"><SkipBack className="w-4 h-4"/></button>
            <button data-testid={`pw-prev-${game.id}`} onClick={()=>nav('prev')} className="p-1 hover:bg-indigo-50 rounded"><ChevronLeft className="w-4 h-4"/></button>
            <button data-testid={`pw-next-${game.id}`} onClick={()=>nav('next')} className="p-1 hover:bg-indigo-50 rounded"><ChevRight className="w-4 h-4"/></button>
            <button data-testid={`pw-last-${game.id}`} onClick={()=>nav('last')} className="p-1 hover:bg-indigo-50 rounded"><SkipForward className="w-4 h-4"/></button>
            {loading && <Loader2 className="w-3 h-3 animate-spin ml-1"/>}
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold">{index + 1}. {game.white_player} - {game.black_player}</div>
          <div className="text-sm text-slate-500 mb-2">{meta}</div>
          <button data-testid={`pw-pgn-toggle-${game.id}`} onClick={async ()=>{ if (!detail) await ensureDetail(); setShowPgn(v=>!v); }}
            className="text-sm text-slate-600 inline-flex items-center gap-1 hover:text-indigo-600">
            {showPgn ? <ChevronDown className="w-3.5 h-3.5"/> : <ChevronRight className="w-3.5 h-3.5"/>} PGN
          </button>
          {showPgn && <pre className="mt-1 text-[11px] bg-slate-50 rounded p-2 max-h-28 overflow-auto whitespace-pre-wrap">{detail?.pgn || '—'}</pre>}
        </div>
      </div>
    </div>
  );
}

export default function PuzzleActivityWizard({ open, onClose, onSave, onSaveBulk, initial }) {
  const editing = !!initial?.editingId;
  const [step, setStep] = useState(editing ? 'config' : 'select');
  const [view, setView] = useState('databases'); // databases | games
  const [dbs, setDbs] = useState({ mine: [], shared: [], public: [] });
  const [openGroups, setOpenGroups] = useState({ mine: true, shared: false, public: false });
  const [currentDb, setCurrentDb] = useState(null);
  const [games, setGames] = useState([]);
  const [loadingGames, setLoadingGames] = useState(false);
  const [q, setQ] = useState('');
  const [qGames, setQGames] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);

  const [mode, setMode] = useState('single'); // single | bulk
  const [positions, setPositions] = useState([]);
  const [sans, setSans] = useState([]);
  const [posIdx, setPosIdx] = useState(0);
  const [form, setForm] = useState({ title: '', fen: START, solution_moves: '', hint: '', points: 50, max_attempts: 3, time_seconds: 120 });
  // Fase fix-variantes · PGN completo de la partida origen (conserva variantes)
  // y FEN de su raíz. Se usa para derivar accepted_solutions SOLO cuando el FEN
  // del ejercicio coincide con la raíz del PGN (posición inicial del capítulo).
  const [gamePgn, setGamePgn] = useState(null);
  const [rootFen, setRootFen] = useState(null);
  const [bulk, setBulk] = useState({ points: 50, max_attempts: 3, time_seconds: 120 });

  useEffect(() => {
    if (!open) return;
    setStep(editing ? 'config' : 'select');
    setView('databases'); setCurrentDb(null); setGames([]); setSelectedIds([]); setQ('');
    setGamePgn(null); setRootFen(null);
    if (editing && initial?.activity) {
      const a = initial.activity; setMode('single');
      setForm({ title: a.title || '', fen: a.fen || START, solution_moves: (a.solution_moves || []).join(' '), hint: a.hint || '', points: a.points ?? 50, max_attempts: a.max_attempts ?? 3, time_seconds: a.time_seconds ?? 120 });
      setPositions([]); setSans([]);
    } else {
      setForm({ title: '', fen: START, solution_moves: '', hint: '', points: 50, max_attempts: 3, time_seconds: 120 });
      setPositions([]); setSans([]); setPosIdx(0);
    }
  }, [open]); // eslint-disable-line

  const loadDbs = useCallback(async () => {
    try { const { data } = await api.get('/pgn-databases'); setDbs(data); } catch { /* silent */ }
  }, []);
  useEffect(() => { if (open && !editing) loadDbs(); }, [open, editing, loadDbs]);

  const enterDb = async (db) => {
    setCurrentDb(db); setView('games'); setLoadingGames(true); setGames([]); setSelectedIds([]); setQGames('');
    try { const { data } = await api.get(`/pgn-databases/${db.id}/games`, { params: { limit: 200 } }); setGames(data.items || []); }
    catch { setGames([]); } finally { setLoadingGames(false); }
  };

  const shown = qGames ? games.filter(g => `${g.white_player} ${g.black_player} ${g.event} ${g.opening}`.toLowerCase().includes(qGames.toLowerCase())) : games;
  const toggle = (gid) => setSelectedIds(s => s.includes(gid) ? s.filter(x=>x!==gid) : [...s, gid]);
  const selectN = (n) => setSelectedIds(shown.slice(0, n).map(g=>g.id));
  const selectAll = () => setSelectedIds(shown.map(g=>g.id));
  const unselectAll = () => setSelectedIds([]);

  const continueToConfig = async () => {
    if (selectedIds.length === 0) { toast.error('Selecciona al menos una partida'); return; }
    if (selectedIds.length === 1) {
      setMode('single');
      try {
        const { data } = await api.get(`/games/${selectedIds[0]}`);
        setGamePgn(data.pgn || null);
        const c = new Chess(data.starting_fen || undefined);
        const fens = [c.fen()]; const sanList = [];
        for (const m of (data.moves || [])) {
          if (m.fen) { fens.push(m.fen); sanList.push(m.san || '?'); try { c.load(m.fen); } catch { /* ignore */ } continue; }
          try { c.move(m.san); fens.push(c.fen()); sanList.push(m.san); } catch { break; }
        }
        setPositions(fens); setSans(sanList); setRootFen(fens[0] || null);
        const startIdx = Math.max(0, fens.length - 2);
        setPosIdx(startIdx);
        setForm(f => ({ ...f, title: data.opening || data.event || `${data.white_player} vs ${data.black_player}`, fen: fens[startIdx], solution_moves: sanList[startIdx] || '' }));
        setStep('config');
      } catch { toast.error('No se pudo cargar la partida'); }
    } else {
      setMode('bulk'); setBulk({ points: 50, max_attempts: 3, time_seconds: 120 }); setStep('config');
    }
  };

  const gotoIdx = (i) => {
    const clamped = Math.max(0, Math.min(positions.length - 1, i));
    setPosIdx(clamped);
    setForm(f => ({ ...f, fen: positions[clamped], solution_moves: sans[clamped] ? sans[clamped] : f.solution_moves }));
  };

  const submitSingle = () => {
    if (!form.title.trim()) { toast.error('Título requerido'); return; }
    try { new Chess(form.fen); } catch { toast.error('FEN inválido'); return; }
    const solution_moves = form.solution_moves.split(/[\s,]+/).filter(Boolean);
    if (solution_moves.length === 0) { toast.error('Indica al menos una jugada solución'); return; }
    const payload = { type: 'puzzle', title: form.title, fen: form.fen, solution_moves, hint: form.hint, points: Number(form.points)||0, max_attempts: Number(form.max_attempts)||1, time_seconds: Number(form.time_seconds)||60 };
    // Fase fix-variantes · Si el ejercicio parte de la RAÍZ del PGN (posición
    // inicial del capítulo) y hay PGN con variantes, enviarlo para que el
    // backend (_derive_accepted → _san_lines_from_pgn) genere accepted_solutions
    // con TODAS las líneas oficiales. Sólo cuando el FEN coincide con la raíz,
    // para no desalinear PGN y posición. Retrocompatible: sin match, no se envía.
    if (gamePgn && rootFen && form.fen === rootFen) {
      payload.pgn = gamePgn;
    }
    onSave(payload);
  };
  const submitBulk = () => onSaveBulk(selectedIds, { points: Number(bulk.points)||0, max_attempts: Number(bulk.max_attempts)||1, time_seconds: Number(bulk.time_seconds)||60 });

  const DbList = ({ items }) => (
    items.length === 0 ? <div className="text-xs text-slate-400 px-2 py-1">Vacío</div> :
    items.map(db => (
      <button key={db.id} data-testid={`pw-db-${db.id}`} onClick={()=>enterDb(db)}
        className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm hover:bg-indigo-50">
        <Folder className="w-4 h-4 text-slate-500"/>
        <span className="flex-1 truncate text-left">{db.name}</span>
        <span className="text-[10px] text-slate-400 font-mono">{db.games_count ?? 0}</span>
        <ChevronRight className="w-3.5 h-3.5 text-slate-400"/>
      </button>
    ))
  );

  return (
    <Dialog open={open} onOpenChange={(v)=>{ if(!v) onClose(); }}>
      <DialogContent data-testid="puzzle-wizard" className="max-w-4xl p-0 overflow-hidden max-h-[92vh] flex flex-col">
        <DialogTitle className="sr-only">Create Puzzle Activity</DialogTitle>
        <DialogDescription className="sr-only">Selecciona partidas de la base de datos y configura la actividad de puzzle.</DialogDescription>
        <div className="bg-slate-50 border-b px-6 py-3 font-display text-xl font-bold shrink-0">Create Puzzle Activity</div>
        <div className="flex items-center justify-center gap-6 py-4 shrink-0">
          <StepDot n={1} label="Select games" active={step==='select'} done={step==='config' && !editing}/>
          <div className="w-24 h-px bg-slate-300"/>
          <StepDot n={2} label="Configuration" active={step==='config'}/>
        </div>

        {step === 'select' ? (
          view === 'databases' ? (
            <div className="px-6 pb-6 overflow-y-auto">
              <div className="relative mb-3 max-w-xl mx-auto">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                <Input data-testid="pw-search" placeholder="Search database…" value={q} onChange={(e)=>setQ(e.target.value)} className="pl-9 rounded-lg"/>
              </div>
              <div className="max-w-xl mx-auto min-h-[280px]">
                {['mine','shared','public'].map(key => {
                  const items = (dbs[key]||[]).filter(d => !q || (d.name||'').toLowerCase().includes(q.toLowerCase()));
                  return (
                    <div key={key} className="mb-1">
                      <button onClick={()=>setOpenGroups(s=>({...s,[key]:!s[key]}))} className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-slate-100 rounded text-sm font-semibold">
                        {openGroups[key] ? <ChevronDown className="w-3.5 h-3.5"/> : <ChevronRight className="w-3.5 h-3.5"/>}
                        <Folder className="w-4 h-4 text-slate-500"/>
                        {key==='mine'?'My Databases':key==='shared'?'Shared Databases':'Public Databases'}
                        <span className="ml-auto text-xs text-slate-400 font-mono">{items.length}</span>
                      </button>
                      {openGroups[key] && <div className="ml-3 mt-1 space-y-0.5"><DbList items={items}/></div>}
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-start mt-2"><Button variant="outline" onClick={onClose}>Cancelar</Button></div>
            </div>
          ) : (
            <div className="flex flex-col min-h-0 flex-1">
              <div className="px-6 shrink-0">
                <div className="flex items-center gap-3 mb-3">
                  <Button data-testid="pw-back-db" size="sm" variant="outline" onClick={()=>{ setView('databases'); setSelectedIds([]); }}><ArrowLeft className="w-4 h-4 mr-1"/> Back</Button>
                  <span className="font-semibold">{currentDb?.name}</span>
                </div>
                <div className="flex gap-2 mb-2">
                  <Button data-testid="pw-select-10" size="sm" variant="outline" onClick={()=>selectN(10)}>Select 10</Button>
                  <Button data-testid="pw-select-all" size="sm" variant="outline" onClick={selectAll}>Select All</Button>
                  <Button data-testid="pw-unselect-all" size="sm" variant="outline" onClick={unselectAll}>Unselect All</Button>
                  <div className="relative ml-auto w-56">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                    <Input data-testid="pw-games-search" placeholder="Search…" value={qGames} onChange={(e)=>setQGames(e.target.value)} className="pl-9 rounded-lg h-9"/>
                  </div>
                </div>
              </div>
              <div className="px-6 overflow-y-auto flex-1">
                {loadingGames ? <div className="text-sm text-slate-400 flex items-center gap-2 py-8"><Loader2 className="w-4 h-4 animate-spin"/> Cargando partidas…</div> :
                 shown.length === 0 ? <div className="text-sm text-slate-400 py-8 text-center">Sin partidas</div> :
                 shown.map((g, i) => <GameRow key={g.id} game={g} index={i} checked={selectedIds.includes(g.id)} onToggle={toggle}/>)}
              </div>
              <div className="px-6 py-3 border-t flex items-center justify-between shrink-0">
                <span className="text-sm text-slate-500">{selectedIds.length} seleccionada(s)</span>
                <Button data-testid="pw-continue" onClick={continueToConfig} disabled={selectedIds.length===0} className="bg-indigo-600 hover:bg-indigo-700">Continuar →</Button>
              </div>
            </div>
          )
        ) : mode === 'bulk' ? (
          <div className="px-6 pb-6 overflow-y-auto">
            <div className="max-w-md mx-auto space-y-4">
              <div className="rounded-xl bg-indigo-50 text-indigo-800 text-sm p-4">
                Se crearán <b>{selectedIds.length} ejercicios</b>. Cada uno usará la posición inicial de la partida y la solución serán las jugadas de la partida.
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Puntaje</Label><Input data-testid="pw-bulk-points" type="number" value={bulk.points} onChange={e=>setBulk(b=>({...b,points:e.target.value}))}/></div>
                <div><Label>Intentos</Label><Input data-testid="pw-bulk-attempts" type="number" value={bulk.max_attempts} onChange={e=>setBulk(b=>({...b,max_attempts:e.target.value}))}/></div>
                <div><Label>Tiempo (seg)</Label><Input data-testid="pw-bulk-time" type="number" value={bulk.time_seconds} onChange={e=>setBulk(b=>({...b,time_seconds:e.target.value}))}/></div>
              </div>
              <div className="flex justify-between pt-2">
                <Button data-testid="pw-back" variant="outline" onClick={()=>setStep('select')}>Atrás</Button>
                <Button data-testid="pw-bulk-save" onClick={submitBulk} className="bg-indigo-600 hover:bg-indigo-700">Agregar {selectedIds.length} ejercicios</Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="px-6 pb-6 overflow-y-auto grid md:grid-cols-[300px_1fr] gap-6">
            <div>
              <ChessBoardView fen={form.fen} boardWidth={280} arePiecesDraggable={false}/>
              {positions.length > 0 && (
                <div className="flex items-center justify-center gap-1 mt-2">
                  <Button data-testid="pw-cfg-first" size="sm" variant="outline" onClick={()=>gotoIdx(0)}><SkipBack className="w-4 h-4"/></Button>
                  <Button data-testid="pw-cfg-prev" size="sm" variant="outline" onClick={()=>gotoIdx(posIdx-1)}><ChevronLeft className="w-4 h-4"/></Button>
                  <span className="text-xs font-mono text-slate-500 px-2">{posIdx}/{positions.length-1}</span>
                  <Button data-testid="pw-cfg-next" size="sm" variant="outline" onClick={()=>gotoIdx(posIdx+1)}><ChevRight className="w-4 h-4"/></Button>
                  <Button data-testid="pw-cfg-last" size="sm" variant="outline" onClick={()=>gotoIdx(positions.length-1)}><SkipForward className="w-4 h-4"/></Button>
                </div>
              )}
              {positions.length > 0 && sans[posIdx] && (
                <p className="text-xs text-center text-slate-500 mt-2">Solución sugerida: <span className="font-mono font-bold">{sans[posIdx]}</span></p>
              )}
            </div>
            <div className="space-y-3">
              <div><Label>Título</Label><Input data-testid="pw-title" value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))}/></div>
              <div><Label>FEN de la posición</Label><Input data-testid="pw-fen" className="font-mono text-xs" value={form.fen} onChange={e=>setForm(f=>({...f,fen:e.target.value}))}/></div>
              <div><Label>Jugadas solución (SAN)</Label><Input data-testid="pw-solution" className="font-mono" value={form.solution_moves} onChange={e=>setForm(f=>({...f,solution_moves:e.target.value}))} placeholder="Ej: Qxh7+ Kxh7 Rh3#"/></div>
              <div><Label>Pista (opcional)</Label><Input data-testid="pw-hint" value={form.hint} onChange={e=>setForm(f=>({...f,hint:e.target.value}))}/></div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Puntaje</Label><Input data-testid="pw-points" type="number" value={form.points} onChange={e=>setForm(f=>({...f,points:e.target.value}))}/></div>
                <div><Label>Intentos</Label><Input data-testid="pw-attempts" type="number" value={form.max_attempts} onChange={e=>setForm(f=>({...f,max_attempts:e.target.value}))}/></div>
                <div><Label>Tiempo (seg)</Label><Input data-testid="pw-time" type="number" value={form.time_seconds} onChange={e=>setForm(f=>({...f,time_seconds:e.target.value}))}/></div>
              </div>
              <div className="flex justify-between pt-2">
                {!editing ? <Button data-testid="pw-back" variant="outline" onClick={()=>setStep('select')}>Atrás</Button> : <Button variant="outline" onClick={onClose}>Cancelar</Button>}
                <Button data-testid="pw-save" onClick={submitSingle} className="bg-indigo-600 hover:bg-indigo-700">{editing ? 'Guardar' : 'Agregar actividad'}</Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
